import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, Ban, RotateCcw } from "lucide-react";

/**
 * SA-facing delete-management hub:
 *   Tab "Pending Requests" → approve / reject delete-requests from operators.
 *   Tab "Deleted Records"  → restore any soft-deleted transaction.
 */
export default function DeletedRecords() {
  const { user } = useAuth();
  const isSA = !!user?.role?.is_super_admin;
  const isPH = user?.role?.name === "Plant Head/Admin";
  // SA sees everything; PH can approve the modules listed below (must mirror
  // backend MODULES_PH_CAN_APPROVE in /app/backend/routers/delete_requests.py).
  const PH_APPROVAL_MODULES = ["visitor"];
  const [tab, setTab] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [note, setNote] = useState({});

  const load = async () => {
    if (tab === "pending") {
      const r = await api.get("/delete-requests?status=pending");
      setRequests(r.data || []);
    } else {
      const r = await api.get("/deleted-records");
      setDeleted(r.data || []);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  if (!isSA && !isPH) {
    return (
      <div data-testid="deleted-records-page">
        <PageHeader title="Deleted Records" subtitle="Super Admin / Plant Head only"/>
        <div className="p-8 text-[#C62828] text-sm">Access denied.</div>
      </div>
    );
  }
  const canDecide = (mod) => isSA || (isPH && PH_APPROVAL_MODULES.includes(mod));

  const decide = async (rid, action) => {
    try {
      await api.post(`/delete-requests/${rid}/${action}`, { note: note[rid] || "" });
      toast.success(action === "approve" ? "Approved + record deleted" : "Rejected");
      setNote({ ...note, [rid]: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };

  const restore = async (mod, rid) => {
    try {
      await api.post(`/deleted-records/${mod}/${rid}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Restore failed"); }
  };

  return (
    <div data-testid="deleted-records-page">
      <PageHeader title="Deleted Records" subtitle="Approve delete requests · restore soft-deleted entries"/>
      <div className="p-8 space-y-4">
        <div className="flex gap-2">
          {[["pending", `Pending Requests (${requests.length})`], ["trash", "Deleted Records"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`del-tab-${k}`}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm border ${tab === k ? "bg-[#0D2B5E] text-white border-[#0D2B5E]" : "bg-white text-[#0D2B5E] border-[#EEEEEE]"}`}>{l}</button>
          ))}
        </div>

        {tab === "pending" && (
          <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">Module</th>
                  <th className="text-left px-4 py-2">Record</th>
                  <th className="text-left px-4 py-2">Requested by</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-left px-4 py-2">SA note</th>
                  <th className="text-right px-4 py-2">Decision</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r, i) => (
                  <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} data-testid={`pending-${r.id}`}>
                    <td className="px-4 py-2 uppercase tracking-widest text-xs text-[#1565C0]">{r.module.replace("_", " ")}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.record_label}</td>
                    <td className="px-4 py-2 text-xs">{r.requested_by_name} <span className="text-[#0D2B5E]/50">· {r.requested_by_role}</span><br/><span className="text-[10px] text-[#0D2B5E]/50">{(r.requested_at || "").slice(0, 16).replace("T", " ")}</span></td>
                    <td className="px-4 py-2 text-xs">{r.reason}</td>
                    <td className="px-4 py-2"><input value={note[r.id] || ""} onChange={(e) => setNote({ ...note, [r.id]: e.target.value })} placeholder="Optional note…" className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm" data-testid={`note-${r.id}`}/></td>
                    <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">
                      {canDecide(r.module) ? (
                        <>
                          <button onClick={() => decide(r.id, "approve")} className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`approve-${r.id}`}><Check size={11}/>Approve</button>
                          <button onClick={() => decide(r.id, "reject")} className="text-xs bg-[#C62828] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`reject-${r.id}`}><Ban size={11}/>Reject</button>
                        </>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/40" title="Awaiting Super Admin decision">SA only</span>
                      )}
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#0D2B5E]/50">No pending delete requests.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "trash" && (
          <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">Module</th>
                  <th className="text-left px-4 py-2">Record</th>
                  <th className="text-left px-4 py-2">Deleted by</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-right px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {deleted.map((d, i) => (
                  <tr key={`${d.module}-${d.record_id}`} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} data-testid={`deleted-${d.module}-${d.record_id}`}>
                    <td className="px-4 py-2 uppercase tracking-widest text-xs text-[#1565C0]">{d.module.replace("_", " ")}</td>
                    <td className="px-4 py-2 font-mono text-xs">{d.record_label}</td>
                    <td className="px-4 py-2 text-xs">{d.deleted_by} <br/><span className="text-[10px] text-[#0D2B5E]/50">{(d.deleted_at || "").slice(0, 16).replace("T", " ")}</span></td>
                    <td className="px-4 py-2 text-xs">{d.deleted_reason || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => restore(d.module, d.record_id)} className="text-xs bg-[#1565C0] text-white px-3 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`restore-${d.record_id}`}><RotateCcw size={11}/>Restore</button>
                    </td>
                  </tr>
                ))}
                {deleted.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#0D2B5E]/50">No deleted records.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
